/**
 * WizardStepConfirm - Passo 7: Confirmação final antes de gerar o contrato
 * 
 * RELEASE: v1.2.0 - 2026-04-09
 * Alterações:
 *   - Adicionado card de Negócio vinculado (condicional)
 *   - Adicionado card de Documentos anexados (condicional)
 */
import React from 'react';
import { CheckCircle2, FileText, Users, Variable, Briefcase, FileCheck } from 'lucide-react';

interface WizardStepConfirmProps {
  templateName: string;
  title: string;
  signersCount: number;
  /** Total de variáveis com valor preenchido (não vazio) */
  variablesFilled: number;
  /** Total de variáveis identificadas pelo parser no HTML do template */
  variablesTotal: number;
  /** Versão fixada do template (ex: "v3") ou "Mais recente" */
  templateVersion: string;
  /** Título do negócio vinculado (exibido apenas se preenchido) */
  dealTitle?: string;
  /** Quantidade de documentos anexados pelo cliente */
  documentsAttached?: number;
  /** Total de documentos exigidos pelo template */
  documentsRequired?: number;
  /** v2026-05: nome do PDF enviado no wizard (modelos do tipo uploaded_pdf) */
  uploadedPdfName?: string;
}

export function WizardStepConfirm({
  templateName, title, signersCount, variablesFilled, variablesTotal,
  templateVersion, dealTitle, documentsAttached = 0, documentsRequired = 0,
  uploadedPdfName,
}: WizardStepConfirmProps) {
  return (
    <div className="space-y-6 py-4">
      <div className="text-center space-y-2">
        <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
        <h3 className="text-lg font-semibold">Tudo pronto!</h3>
        <p className="text-sm text-muted-foreground">
          Revise os dados abaixo e clique em "Gerar Contrato" para confirmar.
        </p>
      </div>

      <div className="space-y-3 max-w-md mx-auto">
        {/* Título do contrato */}
        <div className="flex items-center gap-3 p-3 rounded-lg border">
          <FileText className="h-5 w-5 text-primary shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Título do contrato</p>
            <p className="text-sm font-medium">{title}</p>
          </div>
        </div>

        {/* Modelo utilizado */}
        <div className="flex items-center gap-3 p-3 rounded-lg border">
          <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Modelo utilizado</p>
            <p className="text-sm font-medium">{templateName}</p>
            <p className="text-xs text-muted-foreground">{templateVersion}</p>
          </div>
        </div>

        {/* Negócio vinculado (condicional) */}
        {dealTitle && (
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <Briefcase className="h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Negócio vinculado</p>
              <p className="text-sm font-medium">{dealTitle}</p>
            </div>
          </div>
        )}

        {/* Variáveis preenchidas — oculto quando o contrato é PDF enviado no wizard */}
        {!uploadedPdfName && (
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <Variable className="h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Variáveis preenchidas</p>
              <p className="text-sm font-medium">{variablesFilled} de {variablesTotal}</p>
            </div>
          </div>
        )}

        {/* v2026-05: PDF anexado no wizard (modelos do tipo uploaded_pdf) */}
        {uploadedPdfName && (
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <FileText className="h-5 w-5 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">PDF do contrato</p>
              <p className="text-sm font-medium truncate">{uploadedPdfName}</p>
            </div>
          </div>
        )}

        {/* Signatários */}
        <div className="flex items-center gap-3 p-3 rounded-lg border">
          <Users className="h-5 w-5 text-muted-foreground shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Signatários</p>
            <p className="text-sm font-medium">{signersCount}</p>
          </div>
        </div>

        {/* Documentos anexados (condicional) */}
        {documentsRequired > 0 && (
          <div className="flex items-center gap-3 p-3 rounded-lg border">
            <FileCheck className="h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Documentos anexados</p>
              <p className="text-sm font-medium">{documentsAttached} de {documentsRequired}</p>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-center text-muted-foreground">
        O contrato será criado como rascunho. Você poderá gerar o PDF e enviá-lo para assinatura posteriormente.
      </p>
    </div>
  );
}